import React from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Grid, IconButton, MenuItem, Select, TextField, Typography } from "@mui/material";
import { MdAdd, MdDelete } from "react-icons/md";
import { labelSx } from "./common";
import { SOURCE_LABELS, keyOf } from "./variables";

export const emptyVariable = { label: "", source: "send", required: true, default: "", description: "" };

/**
 * Checks declared variables against the database values and the values the template uses.
 * used: [{ key, label }] found in the texts / slot mappings.
 * Returns { errors: { var<i>: message }, undeclared: [{ key, label }], unused: [labels] }.
 */
export const checkVariables = (variables, systemVariables, used) => {
  const systemKeys = systemVariables.map((v) => v.name);
  const errors = {};
  const seen = new Set();
  variables.forEach((v, i) => {
    const key = keyOf(v.label);
    if (!/^[a-z]/.test(key)) errors[`var${i}`] = "Give a label starting with a letter, e.g. Start Date";
    else if (seen.has(key)) errors[`var${i}`] = "Declared twice";
    else if (v.source !== "system" && systemKeys.includes(key)) errors[`var${i}`] = "This is a database value; set its source to From database";
    else if (v.source === "system" && !systemKeys.includes(key)) errors[`var${i}`] = "Not a database value";
    seen.add(key);
  });
  const declared = variables.map((v) => keyOf(v.label)).filter(Boolean);
  const usedKeys = used.map((u) => u.key);
  return {
    errors,
    undeclared: used.filter((u, i) => u.key && !declared.includes(u.key) && !systemKeys.includes(u.key) && usedKeys.indexOf(u.key) === i),
    unused: variables.filter((v) => keyOf(v.label) && !usedKeys.includes(keyOf(v.label))).map((v) => v.label),
  };
};

// The variables as the API takes them.
export const variablesPayload = (variables) =>
  variables.map((v) => ({
    label: v.label.trim(),
    source: v.source,
    required: !!v.required,
    ...(String(v.default ?? "").trim() !== "" && { default: String(v.default) }),
    ...(String(v.description ?? "").trim() !== "" && { description: v.description.trim() }),
  }));

// Variables from the API, ready to edit.
export const editableVariables = (variables) =>
  (variables || []).map((v) => ({ ...emptyVariable, ...v, label: v.label || v.name, default: v.default ?? "", description: v.description ?? "" }));

/**
 * The "Variables" section: one row per declared variable, with notices for values used but not declared
 * and declared but not used.
 */
export default function VariablesEditor({ variables, onChange, systemVariables, used, errors = {}, help, showUndeclaredError }) {
  const check = checkVariables(variables, systemVariables, used);
  const update = (i, field, value) => onChange(variables.map((v, j) => (j === i ? { ...v, [field]: value } : v)));

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={labelSx}>Variables</Typography>
        <Button size="small" startIcon={<MdAdd />} onClick={() => onChange([...variables, { ...emptyVariable }])}>Add Variable</Button>
      </Box>
      {help && <Typography variant="body2" sx={{ color: "#555", mb: 1 }}>{help}</Typography>}
      {check.undeclared.length > 0 && (
        <Alert
          severity={showUndeclaredError ? "error" : "warning"}
          sx={{ mb: 1 }}
          action={<Button size="small" onClick={() => onChange([...variables, ...check.undeclared.map((u) => ({ ...emptyVariable, label: u.label }))])}>Add them</Button>}
        >
          Used but not declared yet: {check.undeclared.map((u) => u.label).join(", ")}
        </Alert>
      )}
      {check.unused.length > 0 && <Alert severity="info" sx={{ mb: 1 }}>Declared but not used: {check.unused.join(", ")}</Alert>}
      {variables.map((v, i) => (
        <Grid container spacing={1} key={i} sx={{ mb: 1, alignItems: "flex-start" }}>
          <Grid item xs={12} md={2.5}>
            <Select fullWidth size="small" value={v.source} onChange={(e) => update(i, "source", e.target.value)}>
              {Object.entries(SOURCE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            {v.source === "system" ? (
              <Select fullWidth size="small" displayEmpty value={systemVariables.some((s) => s.label === v.label) ? v.label : ""} onChange={(e) => update(i, "label", e.target.value)} error={!!(errors[`var${i}`] || check.errors[`var${i}`])}>
                <MenuItem value="" disabled>Choose the database value</MenuItem>
                {systemVariables.map((s) => <MenuItem key={s.name} value={s.label}>{s.label}</MenuItem>)}
              </Select>
            ) : (
              <TextField fullWidth size="small" label="Label" placeholder="e.g. Start Date" value={v.label} onChange={(e) => update(i, "label", e.target.value)} error={!!(errors[`var${i}`] || (v.label && check.errors[`var${i}`]))} helperText={errors[`var${i}`] || (v.label ? check.errors[`var${i}`] : "")} />
            )}
          </Grid>
          <Grid item xs={6} md={1.5}>
            <FormControlLabel control={<Checkbox size="small" checked={!!v.required} onChange={(e) => update(i, "required", e.target.checked)} />} label="Required" />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" label="Default" value={v.default} onChange={(e) => update(i, "default", e.target.value)} />
          </Grid>
          <Grid item xs={10} md={2.5}>
            <TextField fullWidth size="small" label="Help text" value={v.description} onChange={(e) => update(i, "description", e.target.value)} />
          </Grid>
          <Grid item xs={2} md={0.5}>
            <IconButton onClick={() => onChange(variables.filter((_, j) => j !== i))}><MdDelete /></IconButton>
          </Grid>
        </Grid>
      ))}
    </Box>
  );
}
